/**
 * Responsive CSS - Mobile First Approach
 * 
 * These styles are applied via data attributes on React Native Web components.
 * Usage: <View dataSet={{ responsive: 'hero-split' }} />
 * This creates: <div data-responsive="hero-split" />
 * 
 * Breakpoints (matching your JS breakpoints):
 * - Default: mobile (≤640px)
 * - sm: 641px - 980px (tablet)
 * - md: 981px - 1240px (small desktop)
 * - lg: >1240px (large desktop)
 */

/* ===========================================
   SECTION TITLES
   =========================================== */

/* Section title - displayMedium equivalent */
/* Mobile default (≤640px): 32px */
[data-responsive="section-title"] {
  font-size: 32px !important;
  line-height: 36px !important;
}

/* Desktop (>640px): 48px */
@media (min-width: 641px) {
  [data-responsive="section-title"] {
    font-size: 48px !important;
    line-height: 53px !important;
  }
}

/* "How it works" section title has different breakpoints */
[data-responsive="section-title-how"] {
  font-size: 32px !important;
  line-height: 44px !important;
}

@media (min-width: 641px) {
  [data-responsive="section-title-how"] {
    font-size: 48px !important;
    line-height: 52px !important;
  }
}

/* CTA section h3 title */
[data-responsive="cta-title"] {
  font-size: 32px !important;
  line-height: 40px !important;
}

@media (min-width: 641px) {
  [data-responsive="cta-title"] {
    font-size: 48px !important;
    line-height: 56px !important;
  }
}


/* ===========================================
   HERO SECTION
   =========================================== */

/* -----------------------------------------
   Hero Section Spacing - Mobile First
   ----------------------------------------- */

/* Mobile default (≤640px): tighter padding */
[data-responsive="hero-section"] {
  padding-top: 32px !important;
}

/* Desktop (>640px): more breathing room */
@media (min-width: 641px) {
  [data-responsive="hero-section"] {
    padding-top: 72px !important;
  }
}

/* -----------------------------------------
   Hero Typography - fluid scale
   Keeps the headline as two single-line statements plus the question.
   ----------------------------------------- */

[data-responsive="hero-title"] {
  font-size: clamp(16px, 4.8vw, 34px) !important;
  line-height: 1.15 !important;
  letter-spacing: 0px !important;
  white-space: nowrap !important;
}

[data-responsive="hero-rotating-word"] {
  display: inline-grid !important;
  grid-template-areas: "word";
  min-width: calc(clamp(16px, 4.8vw, 34px) * 6) !important;
  min-height: calc(clamp(16px, 4.8vw, 34px) * 1.2) !important;
}

/* -----------------------------------------
   Hero Underline - Mobile First
   Positioned lower on mobile to not appear as strikethrough
   ----------------------------------------- */

/* Mobile default (≤640px): bottom -2 (below text baseline) */
[data-responsive="hero-underline"] {
  bottom: -2px !important;
}

/* Tablet (641-980px): bottom 0 */
@media (min-width: 641px) {
  [data-responsive="hero-underline"] {
    bottom: 0px !important;
  }
}

/* Small desktop (981-1240px): bottom 2 */
@media (min-width: 981px) {
  [data-responsive="hero-underline"] {
    bottom: 2px !important;
  }
}

/* Large desktop (>1240px): bottom 4 (original value) */
@media (min-width: 1241px) {
  [data-responsive="hero-underline"] {
    bottom: 4px !important;
  }
}

/* -----------------------------------------
   Hero Layout - Mobile First
   ----------------------------------------- */

/* Mobile default: stacked vertically */
[data-responsive="hero-split"] {
  flex-direction: column !important;
  align-items: center !important;
  gap: 48px !important;
}

[data-responsive="hero-left"] {
  flex: none !important;
  max-width: 640px !important;
  align-items: center !important;
}

[data-responsive="hero-right"] {
  flex: none !important;
  max-width: 560px !important;
  align-items: center !important;
  border-left-width: 0 !important;
  padding-left: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

[data-responsive="hero-description"] {
  text-align: center !important;
}

[data-responsive="hero-meta"] {
  text-align: center !important;
}

[data-responsive="hero-ctas"] {
  flex-direction: column !important;
  align-items: stretch !important;
}

/* Tablet and up (>640px): minor adjustments */
@media (min-width: 641px) {
  [data-responsive="hero-ctas"] {
    flex-direction: row !important;
    align-items: center !important;
  }
}

/* Desktop (≥1024px): side-by-side layout */
@media (min-width: 1024px) {
  [data-responsive="hero-split"] {
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 56px !important;
  }

  [data-responsive="hero-left"] {
    flex: 1 !important;
    max-width: none !important;
    align-items: center !important;
    justify-content: center !important;
  }

  [data-responsive="hero-right"] {
    flex: 1 !important;
    max-width: 480px !important;
    border-left-width: 1px !important;
    border-left-color: #e0e2e7 !important;
    border-left-style: solid !important;
    padding-left: 56px !important;
    padding-top: 16px !important;
    padding-bottom: 16px !important;
    align-items: flex-start !important;
  }

  [data-responsive="hero-description"] {
    text-align: left !important;
  }

  [data-responsive="hero-meta"] {
    text-align: left !important;
  }
}

/* Medium desktop: keep the two-column hero balanced before wide desktop. */
@media (min-width: 1024px) and (max-width: 1240px) {
  [data-responsive="hero-title"] {
    font-size: clamp(30px, calc(3.2vw - 0.5px), 38px) !important;
  }

  [data-responsive="hero-rotating-word"] {
    min-width: calc(clamp(30px, calc(3.2vw - 0.5px), 38px) * 6) !important;
    min-height: calc(clamp(30px, calc(3.2vw - 0.5px), 38px) * 1.2) !important;
  }

  [data-responsive="hero-split"] {
    gap: 40px !important;
  }

  [data-responsive="hero-left"] {
    flex-grow: 1 !important;
    flex-shrink: 1 !important;
    flex-basis: 0% !important;
  }

  [data-responsive="hero-right"] {
    flex-grow: 0 !important;
    flex-shrink: 1 !important;
    flex-basis: clamp(340px, 34vw, 400px) !important;
    max-width: 400px !important;
    padding-left: 36px !important;
  }
}

/* Wide desktop: let the headline return to the full display size. */
@media (min-width: 1241px) {
  [data-responsive="hero-title"] {
    font-size: clamp(36px, 3.2vw, 40px) !important;
  }

  [data-responsive="hero-rotating-word"] {
    min-width: calc(clamp(36px, 3.2vw, 40px) * 6) !important;
    min-height: calc(clamp(36px, 3.2vw, 40px) * 1.2) !important;
  }
}


/* ===========================================
   HEADER
   =========================================== */

/* Mobile default: hide inline nav, show hamburger */
[data-responsive="header-inline-nav"] {
  display: none !important;
}

[data-responsive="header-hamburger"] {
  display: flex !important;
}

/* Desktop (≥1024px): show inline nav, hide hamburger.
   Longer translated labels need the compact menu at tablet widths. */
@media (min-width: 1024px) {
  [data-responsive="header-inline-nav"] {
    display: flex !important;
  }

  [data-responsive="header-hamburger"] {
    display: none !important;
  }
}

/* -----------------------------------------
   Header Title - Mobile First
   Breakpoints: <400 (fluid ≤28px) | 400-699 (32px) | ≥700 (42px)
   ----------------------------------------- */

[data-responsive="header-title"] {
  display: block !important;
  max-width: 100% !important;
  min-width: 0 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  font-family: Inter, Inter-fallback, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  font-size: clamp(12px, 11.5vw, 28px) !important;
  line-height: 1.1 !important;
  letter-spacing: 1px !important;
  font-weight: 900 !important;  /* Black */
  margin-bottom: 0 !important;  /* Remove margin when subtitle hidden */
}

/* Small-medium (400-699px): 32px */
@media (min-width: 400px) {
  [data-responsive="header-title"] {
    font-size: 32px !important;
    line-height: 36px !important;
    letter-spacing: 1px !important;
    font-weight: 900 !important;
    margin-bottom: 0 !important;  /* Still no subtitle */
  }
}

/* Desktop (≥700px): 42px - subtitle visible */
@media (min-width: 700px) {
  [data-responsive="header-title"] {
    font-size: 42px !important;
    line-height: 47px !important;
    letter-spacing: 0.5px !important;
    font-weight: 900 !important;
    margin-bottom: 4px !important;  /* Space for subtitle */
  }
}

/* -----------------------------------------
   Header Subtitle - Hidden on mobile
   ----------------------------------------- */

/* Mobile default: hidden */
[data-responsive="header-subtitle"] {
  display: none !important;
}

/* Desktop (≥700px): visible */
@media (min-width: 700px) {
  [data-responsive="header-subtitle"] {
    display: block !important;
  }
}

/* Show the expanded SEO phrase only when the header has enough room.
   Tablet-with-hamburger has space; mid-width desktop nav does not. */
[data-responsive="header-subtitle-expanded"] {
  display: none !important;
}

@media (min-width: 700px) and (max-width: 1023px), (min-width: 1120px) {
  [data-responsive="header-subtitle-expanded"] {
    display: inline !important;
    margin-left: 3px !important;
    margin-right: 3px !important;
  }
}

/* -----------------------------------------
   Header Logo - Mobile First
   ----------------------------------------- */

/* Mobile default (<400px): 60px */
[data-responsive="header-logo"] {
  width: 60px !important;
  height: 60px !important;
  margin-right: 4px !important;
}

/* Small-medium (400-699px): 70px */
@media (min-width: 400px) {
  [data-responsive="header-logo"] {
    width: 70px !important;
    height: 70px !important;
    margin-right: 8px !important;
  }
}

/* Desktop (≥700px): 80px */
@media (min-width: 700px) {
  [data-responsive="header-logo"] {
    width: 80px !important;
    height: 80px !important;
    margin-right: 12px !important;
  }
}

/* ===========================================
   FOOTER
   =========================================== */

[data-responsive="footer-container"] {
  padding-left: 16px !important;
  padding-right: 16px !important;
  padding-top: 22px !important;
  padding-bottom: 18px !important;
}

@media (min-width: 390px) {
  [data-responsive="footer-container"] {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }
}

@media (min-width: 1120px) {
  [data-responsive="footer-container"] {
    padding-left: 24px !important;
    padding-right: 24px !important;
    padding-top: 24px !important;
    padding-bottom: 24px !important;
  }
}

[data-responsive="footer-top-row"] {
  flex-direction: column !important;
  align-items: stretch !important;
  gap: 24px !important;
  padding-bottom: 18px !important;
}

[data-responsive="footer-brand"] {
  flex: 0 1 auto !important;
  min-width: 0 !important;
  gap: 16px !important;
}

[data-responsive="footer-brand-title"] {
  font-size: 24px !important;
  line-height: 30px !important;
}

@media (min-width: 390px) {
  [data-responsive="footer-brand-title"] {
    font-size: 28px !important;
    line-height: 34px !important;
  }
}

[data-responsive="footer-center-links"] {
  flex-direction: column !important;
  flex-wrap: nowrap !important;
  gap: 30px !important;
  justify-content: flex-start !important;
  padding-right: 0 !important;
  width: 100% !important;
}

@media (max-width: 389px) {
  [data-responsive="footer-center-links"] {
    gap: 24px !important;
  }
}

[data-responsive="footer-link-column"] {
  gap: 10px !important;
  min-width: 0 !important;
  width: 100% !important;
}

@media (max-width: 389px) {
  [data-responsive="footer-link-column"] {
    gap: 8px !important;
  }
}

[data-responsive="footer-column-heading"] {
  font-size: 13px !important;
  letter-spacing: 0.8px !important;
  margin-bottom: 6px !important;
}

@media (max-width: 389px) {
  [data-responsive="footer-column-heading"] {
    font-size: 12px !important;
    letter-spacing: 0.6px !important;
  }
}

[data-responsive="footer-nav-link"] {
  font-size: 15px !important;
  line-height: 22px !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  padding-top: 3px !important;
  padding-bottom: 3px !important;
}

@media (max-width: 389px) {
  [data-responsive="footer-nav-link"] {
    font-size: 14px !important;
    line-height: 20px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
  }
}

[data-responsive="footer-social-icons"] {
  justify-content: flex-start !important;
}

[data-responsive="footer-social-icon"] {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  padding-right: 8px !important;
}

@media (max-width: 389px) {
  [data-responsive="footer-social-icon"] {
    padding-top: 4px !important;
    padding-bottom: 4px !important;
    padding-right: 6px !important;
  }
}

[data-responsive="footer-separator"] {
  margin-top: 10px !important;
  margin-bottom: 10px !important;
}

[data-responsive="footer-bottom-row"] {
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 10px !important;
  padding-top: 18px !important;
  padding-bottom: 72px !important;
}

[data-responsive="footer-copyright"] {
  font-size: 12px !important;
  line-height: 18px !important;
  text-align: left !important;
}

@media (max-width: 389px) {
  [data-responsive="footer-copyright"] {
    font-size: 11px !important;
    line-height: 16px !important;
  }
}

@media (min-width: 1120px) {
  [data-responsive="footer-top-row"] {
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    gap: 32px !important;
  }

  [data-responsive="footer-brand"] {
    flex: 1 1 0% !important;
    gap: 14px !important;
    min-width: 220px !important;
  }

  [data-responsive="footer-center-links"] {
    flex-direction: row !important;
    justify-content: space-between !important;
    gap: 0 !important;
    width: 820px !important;
  }

  [data-responsive="footer-link-column"] {
    gap: 8px !important;
    min-width: 144px !important;
    width: 144px !important;
  }

  [data-responsive="footer-separator"] {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  [data-responsive="footer-bottom-row"] {
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: center !important;
    gap: 12px !important;
    padding-top: 16px !important;
    padding-bottom: 0 !important;
  }
}


/* ===========================================
   GENERIC RESPONSIVE UTILITIES
   =========================================== */

/* Hide on mobile, show on desktop */
[data-responsive="hide-mobile"] {
  display: none !important;
}

@media (min-width: 981px) {
  [data-responsive="hide-mobile"] {
    display: flex !important;
  }
}

/* Show on mobile, hide on desktop */
[data-responsive="hide-desktop"] {
  display: flex !important;
}

@media (min-width: 981px) {
  [data-responsive="hide-desktop"] {
    display: none !important;
  }
}

/* ===========================================
   DIGEST PREVIEW - Mobile/Desktop layouts
   Uses 768px breakpoint to match original behavior
   =========================================== */

/* Mobile layout: visible by default (mobile-first), hidden on desktop */
[data-responsive="digest-preview-mobile"] {
  display: flex !important;
}

@media (min-width: 768px) {
  [data-responsive="digest-preview-mobile"] {
    display: none !important;
  }
}

/* Desktop layout: hidden by default (mobile-first), visible on desktop */
[data-responsive="digest-preview-desktop"] {
  display: none !important;
}

@media (min-width: 768px) {
  [data-responsive="digest-preview-desktop"] {
    display: flex !important;
  }
}


/* ===========================================
   AUTO-WRAP GRID (for card sections)
   =========================================== */

/* These use CSS Grid for true responsive columns without JS */
[data-responsive="grid-3-2-1"] {
  display: grid !important;
  grid-template-columns: 1fr !important;
  gap: 18px !important;
}

@media (min-width: 641px) {
  [data-responsive="grid-3-2-1"] {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

@media (min-width: 981px) {
  [data-responsive="grid-3-2-1"] {
    grid-template-columns: repeat(3, 1fr) !important;
  }
}

[data-responsive="grid-4-2-1"] {
  display: grid !important;
  grid-template-columns: 1fr !important;
  gap: 18px !important;
}

@media (min-width: 641px) {
  [data-responsive="grid-4-2-1"] {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

@media (min-width: 981px) {
  [data-responsive="grid-4-2-1"] {
    grid-template-columns: repeat(4, 1fr) !important;
  }
}


/* ===========================================
   SOURCES CARD
   =========================================== */

/* Mobile default: a compact two-column logo card */
[data-responsive="sources-card"] {
  padding: 10px !important;
}

/* Tablet and desktop: give the marks more breathing room */
@media (min-width: 641px) {
  [data-responsive="sources-card"] {
    padding: 14px !important;
  }
}

/* Source logo image sizing */
[data-responsive~="source-logo"] {
  width: 100% !important;
  max-width: 136px !important;
  height: 48px !important;
}

/* ChemRxiv needs a slight size boost to match optical weight */
[data-responsive~="source-logo-chemrxiv"] {
  max-width: 150px !important;
  height: 54px !important;
}

/* PubMed's official wordmark has a wider aspect ratio than the preprint marks */
[data-responsive~="source-logo-pubmed"] {
  max-width: 142px !important;
  height: 52px !important;
}

/* Sources logo container: equal columns prevent small marks from looking stranded */
[data-responsive="sources-logos-container"] {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 8px !important;
}

/* Individual tiles normalize the very different source-logo proportions */
[data-responsive="source-logo-wrapper"] {
  align-items: center !important;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%) !important;
  border: 1px solid #edf0f3 !important;
  border-radius: 11px !important;
  display: flex !important;
  justify-content: center !important;
  max-width: none !important;
  min-height: 84px !important;
  min-width: 0 !important;
  padding: 12px 10px !important;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease !important;
}

@media (hover: hover) {
  [data-responsive="source-logo-wrapper"]:hover {
    border-color: #d8dee6 !important;
    box-shadow: 0 8px 20px rgba(11, 16, 32, 0.06) !important;
    transform: translateY(-2px) !important;
  }
}

/* Tablet: three balanced columns */
@media (min-width: 641px) {
  [data-responsive="sources-logos-container"] {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 10px !important;
  }

  [data-responsive="source-logo-wrapper"] {
    min-height: 92px !important;
    padding: 14px 12px !important;
  }

  [data-responsive~="source-logo"] {
    max-width: 150px !important;
    height: 54px !important;
  }

  [data-responsive~="source-logo-chemrxiv"] {
    max-width: 168px !important;
    height: 58px !important;
  }

  [data-responsive~="source-logo-pubmed"] {
    max-width: 158px !important;
    height: 56px !important;
  }
}

/* Desktop: keep all six sources on one clean rail */
@media (min-width: 981px) {
  [data-responsive="sources-logos-container"] {
    grid-template-columns: repeat(6, minmax(0, 1fr)) !important;
  }

  [data-responsive="source-logo-wrapper"] {
    min-height: 102px !important;
  }
}


/* ===========================================
   DIGEST CARDS
   =========================================== */

/* Mobile default: full width */
[data-responsive="digest-card"] {
  width: 100% !important;
  flex-shrink: 1 !important;
}

/* Desktop (>980px): flex grow */
@media (min-width: 981px) {
  [data-responsive="digest-card"] {
    flex: 1 !important;
    width: auto !important;
  }
}


/* ===========================================
   CTA SECTION
   =========================================== */


/* CTA content layout - Mobile default: column */
[data-responsive="cta-content"] {
  flex-direction: column !important;
}

/* Desktop (>980px): row layout */
@media (min-width: 981px) {
  [data-responsive="cta-content"] {
    flex-direction: row !important;
  }
}

/* CTA right column - Mobile: completely hidden, Desktop: visible */
[data-responsive="cta-right-column"] {
  display: none !important;
  height: 0 !important;
  flex: 0 !important;
  overflow: hidden !important;
}

@media (min-width: 981px) {
  [data-responsive="cta-right-column"] {
    display: flex !important;
    height: 100% !important;
    flex: 1 !important;
    overflow: visible !important;
  }
}

/* CTA left column - Mobile default: full width, top-aligned */
[data-responsive="cta-left-column"] {
  max-width: 100% !important;
  padding-left: 24px !important;
  padding-right: 24px !important;
  padding-top: 40px !important;
  padding-bottom: 32px !important;
  justify-content: flex-start !important;
}

/* Desktop (>980px): half width, restore padding */
@media (min-width: 981px) {
  [data-responsive="cta-left-column"] {
    max-width: 50% !important;
    padding-left: 48px !important;
    padding-right: 0 !important;
    padding-top: 48px !important;
    padding-bottom: 48px !important;
  }
}

/* CTA buttons - Mobile default: column */
[data-responsive="cta-buttons"] {
  flex-direction: column !important;
  align-self: flex-start !important;
}

/* Desktop (>640px): row */
@media (min-width: 641px) {
  [data-responsive="cta-buttons"] {
    flex-direction: row !important;
    align-self: auto !important;
  }
}


/* ===========================================
   ADDITIONAL GRIDS
   =========================================== */

/* Grid 3-1-1 (used by "Protect Your Position" section) */
[data-responsive="grid-3-1-1"] {
  display: grid !important;
  grid-template-columns: 1fr !important;
  gap: 18px !important;
}

@media (min-width: 981px) {
  [data-responsive="grid-3-1-1"] {
    grid-template-columns: repeat(3, 1fr) !important;
  }
}


/* ===========================================
   DIGEST ISSUE HEADER
   =========================================== */

/* Title row: Mobile default - stack vertically */
[data-responsive="digest-header-title-row"] {
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 12px !important;
}

/* Desktop (>640px): side by side */
@media (min-width: 641px) {
  [data-responsive="digest-header-title-row"] {
    flex-direction: row !important;
    align-items: flex-start !important;
    justify-content: space-between !important;
    gap: 16px !important;
  }
}

/* Title text: Mobile default - smaller font */
[data-responsive="digest-header-title"] {
  font-size: 22px !important;
  line-height: 28px !important;
  /* User/project names may contain long unbroken identifiers or CJK text. */
  overflow-wrap: anywhere !important;
}

/* Desktop (>640px): larger font */
@media (min-width: 641px) {
  [data-responsive="digest-header-title"] {
    font-size: 28px !important;
    line-height: 36px !important;
  }
}

/* Buttons container */
[data-responsive="digest-header-buttons"] {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: wrap !important;
  gap: 8px !important;
}


/* ===========================================
   SETTINGS PAGE
   =========================================== */

[data-responsive="settings-header"] {
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 16px !important;
}

@media (min-width: 641px) {
  [data-responsive="settings-header"] {
    flex-direction: row !important;
    align-items: flex-end !important;
    justify-content: space-between !important;
  }
}

[data-responsive="settings-form-grid"] {
  display: flex !important;
  flex-direction: column !important;
  gap: 16px !important;
}

/* Stacked fields must retain their content height when translated help wraps.
   React Native Web's flex: 1 otherwise compresses these column children. */
[data-responsive="settings-form-grid"] > * {
  flex: none !important;
  min-width: 0 !important;
}

@media (min-width: 860px) {
  [data-responsive="settings-form-grid"] {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    align-items: start !important;
    gap: 16px !important;
  }

  [data-responsive="settings-form-span-all"] {
    grid-column: 1 / -1 !important;
    width: 100% !important;
  }
}


/* ===========================================
   PAPER CARD
   =========================================== */

/* Paper card content: Mobile default - more bottom padding for arrow clearance */
[data-responsive="paper-card-content"] {
  padding-right: 24px !important;
  padding-bottom: 28px !important;
}

/* Desktop (>640px): original padding */
@media (min-width: 641px) {
  [data-responsive="paper-card-content"] {
    padding-right: 40px !important;
    padding-bottom: 4px !important;
  }
}

/* Paper card metadata: Mobile default - wrap to multiple lines */
[data-responsive="paper-card-metadata"] {
  flex-wrap: wrap !important;
  width: 100% !important;
  gap: 6px !important;
  row-gap: 8px !important;
}

/* Desktop (>640px): tighter spacing */
@media (min-width: 641px) {
  [data-responsive="paper-card-metadata"] {
    flex-wrap: wrap !important;
    gap: 8px !important;
  }
}


/* ===========================================
   PROCESSING MODAL
   =========================================== */

/* Mobile default: stacked vertically */
[data-responsive="processing-modal-layout"] {
  flex-direction: column !important;
  align-items: center !important;
  gap: 16px !important;
  width: 100% !important;
}

[data-responsive="processing-modal-left"] {
  width: 100% !important;
  max-width: 400px !important;
}

[data-responsive="processing-modal-right"] {
  width: 100% !important;
  max-width: 400px !important;
}

/* Desktop (>980px): side-by-side layout */
@media (min-width: 981px) {
  [data-responsive="processing-modal-layout"] {
    flex-direction: row !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 48px !important;
    max-width: 900px !important;
  }

  [data-responsive="processing-modal-left"] {
    flex: 1 !important;
    max-width: 420px !important;
  }

  [data-responsive="processing-modal-right"] {
    flex: 1 !important;
    max-width: 400px !important;
  }
}
